import React, { useState, useEffect } from "react";
import axios from "axios";
import CustomNavbar from "./CustomPermissions/CustomNavbar";
import { getApiUrl, API_BASE_URL } from "../config/api";
import LoadingSpinner from "./common/LoadingSpinner";
import ErrorMessage from "./common/ErrorMessage";

const AnalyticsDashboard = () => {
  const [hrData, setHrData] = useState([]);
  const [docladderData, setDocladderData] = useState([]);
  const [teamLeaderData, setTeamLeaderData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedView, setSelectedView] = useState("overview");
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [monthLabel, setMonthLabel] = useState("Current Month");

  const customPermissions = JSON.parse(localStorage.getItem("user_defined_permissions") || "[]");
  const isSuperuser = localStorage.getItem("is_superuser") === "true";
  const canViewPace = isSuperuser || customPermissions.includes("Pace User Wise Analytics");
  const canViewDocladder = isSuperuser || customPermissions.includes("Docladder User Wise Analytics");
  const canViewTeamLeader = isSuperuser || customPermissions.includes("Team Leaders Wise Analytics");

  useEffect(() => {
    fetchAllData();
  }, [selectedMonth, selectedYear]);

  const fetchAllData = async () => {
    setLoading(true);
    setError(null);

    try {
      const accessToken = localStorage.getItem("access_token");
      const today = new Date();
      const isCurrentMonth = selectedMonth === (today.getMonth() + 1) && selectedYear === today.getFullYear();

      if (isCurrentMonth) {
        setMonthLabel("Current Month");
      } else {
        setMonthLabel(`${getMonthName(selectedMonth)} ${selectedYear}`);
      }

      const headers = { Authorization: `Bearer ${accessToken}` };

      // Fetch HR Associates data
      if (canViewPace) {
        const hrEndpoint = isCurrentMonth ? "/GetAllRecord/" : "/GetPreviousMonthRecords/";
        const hrResponse = await axios.get(getApiUrl(hrEndpoint), { headers });
        setHrData(hrResponse.data.monthly_records || []);
      } else {
        setHrData([]);
      }

      // Fetch Docladder data
      if (canViewDocladder) {
        const docEndpoint = isCurrentMonth ? "/Docladder/daily-user-records/" : "/Docladder/PrevMonth_records_list/";
        const docladderResponse = await axios.get(getApiUrl(docEndpoint), { headers });
        setDocladderData(docladderResponse.data.monthly_records || []);
      } else {
        setDocladderData([]);
      }

      // Fetch Team Leaders data
      if (canViewTeamLeader) {
        let leaderEndpoint;
        let params = {};

        if (isCurrentMonth) {
          leaderEndpoint = "/LeadersTable/leaders/";
        } else {
          leaderEndpoint = "/LeadersTable/get_leaders_performance_by_date/";
          params = { month: selectedMonth, year: selectedYear };
        }

        const teamLeaderResponse = await axios.get(getApiUrl(leaderEndpoint), {
          headers,
          params: params
        });

        setTeamLeaderData(teamLeaderResponse.data.leaders || []);
      } else {
        setTeamLeaderData([]);
      }
    } catch (err) {
      console.error("Error fetching analytics data:", err);
      setError("Failed to load analytics data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getMonthName = (month) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months[month - 1];
  };

  const handleMonthChange = (direction) => {
    if (direction === 'prev') {
      if (selectedMonth === 1) {
        setSelectedMonth(12);
        setSelectedYear(selectedYear - 1);
      } else {
        setSelectedMonth(selectedMonth - 1);
      }
    } else {
      const today = new Date();
      if (selectedMonth === 12) {
        setSelectedMonth(1);
        setSelectedYear(selectedYear + 1);
      } else {
        setSelectedMonth(selectedMonth + 1);
      }
    }
  };

  const isCurrentMonth = () => {
    const today = new Date();
    return selectedMonth === (today.getMonth() + 1) && selectedYear === today.getFullYear();
  };

  const isFutureMonth = () => {
    const today = new Date();
    const selected = new Date(selectedYear, selectedMonth - 1);
    return selected > today;
  };

  // Calculate summary statistics
  const calculateStats = (data) => {
    const totalForwarded = data.reduce((acc, curr) => acc + (curr.resume_forwarded || 0), 0);
    const totalPipeline = data.reduce((acc, curr) => acc + (curr.pipeline || 0), 0);
    const totalTurnedUp = data.reduce((acc, curr) => acc + (curr.turned_up || 0), 0);
    const totalSelected = data.reduce((acc, curr) => acc + (curr.selected || 0), 0);
    const totalJoined = data.reduce((acc, curr) => acc + (curr.joined || 0), 0);
    const totalBackout = data.reduce((acc, curr) => acc + (curr.backout || 0), 0);
    const totalRevenue = data.reduce((acc, curr) => acc + (parseFloat(curr.revenue) || 0), 0);

    const totalCandidates = totalForwarded + totalPipeline + totalTurnedUp + totalSelected;
    const avgConversionRate = totalCandidates > 0 ? ((totalSelected / totalCandidates) * 100).toFixed(2) : 0;
    const avgPipelineConversion = totalPipeline > 0 ? ((totalTurnedUp / totalPipeline) * 100).toFixed(2) : 0;

    return {
      totalForwarded,
      totalPipeline,
      totalTurnedUp,
      totalSelected,
      totalJoined,
      totalBackout,
      totalRevenue: totalRevenue.toFixed(2),
      avgConversionRate,
      avgPipelineConversion,
      totalUsers: data.length,
    };
  };

  // Calculate Team Leader stats separately (they use points system)
  const calculateTeamLeaderStats = (data) => {
    const totalLeaders = data.length;
    const totalPoints = data.reduce((acc, curr) => acc + (parseFloat(curr.value_points) || 0), 0);
    const totalTargetPoints = data.reduce((acc, curr) => acc + (parseFloat(curr.targets_points_sum) || 0), 0);
    const avgPerformance = totalTargetPoints > 0 ? ((totalPoints / totalTargetPoints) * 100).toFixed(2) : 0;

    return {
      totalUsers: totalLeaders,
      totalPoints: totalPoints.toFixed(2),
      totalTargetPoints: totalTargetPoints.toFixed(2),
      avgPerformance,
    };
  };

  const hrStats = calculateStats(hrData);
  const docladderStats = calculateStats(docladderData);
  const teamLeaderStats = calculateTeamLeaderStats(teamLeaderData);
  const combinedStats = calculateStats([...hrData, ...docladderData]);

  if (loading) {
    return (
      <div style={{ display: "flex" }}>
        <CustomNavbar />
        <div className="flex-1">
          <LoadingSpinner message="Loading analytics data..." />
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex" }}>
      <CustomNavbar />
      <div className="flex-1 p-6 bg-gradient-to-br from-gray-50 to-blue-50 min-h-screen">
        <div className="max-w-7xl mx-auto">
          {/* Header with Month Selector */}
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              📊 Analytics Dashboard
            </h1>

            {/* Month Selector */}
            <div className="bg-white rounded-lg shadow-md p-3 flex items-center gap-3">
              <button
                onClick={() => handleMonthChange('prev')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-md transition-colors disabled:opacity-50"
                disabled={loading}
              >
                ← Prev
              </button>
              <div className="text-center px-4">
                <div className="text-lg font-bold text-gray-800">{monthLabel}</div>
                <div className="text-xs text-gray-500">{selectedYear}</div>
              </div>
              <button
                onClick={() => handleMonthChange('next')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-md transition-colors disabled:opacity-50"
                disabled={loading || isFutureMonth()}
              >
                Next →
              </button>
              {!isCurrentMonth() && (
                <button
                  onClick={() => {
                    const today = new Date();
                    setSelectedMonth(today.getMonth() + 1);
                    setSelectedYear(today.getFullYear());
                  }}
                  className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-md transition-colors ml-2"
                >
                  Today
                </button>
              )}
            </div>
          </div>

          {error && <ErrorMessage message={error} onDismiss={() => setError(null)} />}

          {/* View Selector */}
          <div className="bg-white rounded-lg shadow-lg p-4 mb-6 border-t-4 border-blue-500">
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setSelectedView("overview")}
                className={`px-4 py-2 rounded-md transition-colors ${selectedView === "overview"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
              >
                📈 Overview
              </button>
              {canViewPace && (
                <button
                  onClick={() => setSelectedView("hr")}
                  className={`px-4 py-2 rounded-md transition-colors ${selectedView === "hr"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                >
                  👔 HR Associates
                </button>
              )}
              {canViewDocladder && (
                <button
                  onClick={() => setSelectedView("docladder")}
                  className={`px-4 py-2 rounded-md transition-colors ${selectedView === "docladder"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                >
                  🪜 Docladder
                </button>
              )}
              {canViewTeamLeader && (
                <button
                  onClick={() => setSelectedView("teamleader")}
                  className={`px-4 py-2 rounded-md transition-colors ${selectedView === "teamleader"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                >
                  👔 Team Leaders
                </button>
              )}
            </div>
          </div>

          {/* Overview Stats Cards */}
          {selectedView === "overview" && (
            <>
              <h2 className="text-2xl font-bold text-gray-700 mb-4">Overall Statistics</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <StatCard
                  title="Total Users"
                  value={combinedStats.totalUsers}
                  icon="👥"
                  color="blue"
                />
                <StatCard
                  title="Total Resume Forwarded"
                  value={combinedStats.totalForwarded}
                  icon="📨"
                  color="purple"
                />
                <StatCard
                  title="Total Pipeline"
                  value={combinedStats.totalPipeline}
                  icon="⏳"
                  color="yellow"
                />
                <StatCard
                  title="Total Selected"
                  value={combinedStats.totalSelected}
                  icon="✅"
                  color="green"
                />
                <StatCard
                  title="Total Joined"
                  value={combinedStats.totalJoined}
                  icon="🤝"
                  color="green"
                />
                <StatCard
                  title="Total Backout"
                  value={combinedStats.totalBackout}
                  icon="❌"
                  color="red"
                />
                <StatCard
                  title="Total Revenue"
                  value={`${combinedStats.totalRevenue} lac`}
                  icon="💰"
                  color="green"
                />
                <StatCard
                  title="Conversion Rate"
                  value={`${combinedStats.avgConversionRate}%`}
                  icon="📊"
                  color="indigo"
                />
                <StatCard
                  title="Pipeline Conversion"
                  value={`${combinedStats.avgPipelineConversion}%`}
                  icon="🎯"
                  color="teal"
                />
              </div>

              {/* Department Comparison */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                {canViewPace && <DepartmentCard title="HR Associates" stats={hrStats} color="blue" />}
                {canViewDocladder && <DepartmentCard title="Docladder Team" stats={docladderStats} color="purple" />}
                {canViewTeamLeader && <TeamLeaderCard title="Team Leaders" stats={teamLeaderStats} color="indigo" />}
                {!canViewPace && !canViewDocladder && !canViewTeamLeader && (
                  <div className="col-span-full text-center text-gray-500 py-6">
                    You do not have access to any analytics sections.
                  </div>
                )}
              </div>
            </>
          )}

          {/* HR Associates View */}
          {selectedView === "hr" && (
            canViewPace ? (
              <>
                <h2 className="text-2xl font-bold text-gray-700 mb-4">HR Associates Analytics</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <StatCard title="Total Users" value={hrStats.totalUsers} icon="👥" color="blue" />
                  <StatCard title="Resume Forwarded" value={hrStats.totalForwarded} icon="📨" color="purple" />
                  <StatCard title="Selected" value={hrStats.totalSelected} icon="✅" color="green" />
                  <StatCard title="Joined" value={hrStats.totalJoined} icon="🤝" color="green" />
                  <StatCard title="Revenue" value={`${hrStats.totalRevenue} lac`} icon="💰" color="green" />
                  <StatCard title="Conversion Rate" value={`${hrStats.avgConversionRate}%`} icon="📊" color="indigo" />
                </div>

                {/* Top Performers */}
                <div className="mt-6 bg-white rounded-lg shadow-md p-4">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">🏆 Top Performers</h3>
                  <TopPerformersTable data={hrData} />
                </div>
              </>
            ) : (
              <div className="bg-white rounded-lg shadow-md p-6 text-center text-gray-500">
                You do not have access to HR analytics.
              </div>
            )
          )}

          {/* Docladder View */}
          {selectedView === "docladder" && (
            canViewDocladder ? (
              <>
                <h2 className="text-2xl font-bold text-gray-700 mb-4">Docladder Team Analytics</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <StatCard title="Total Users" value={docladderStats.totalUsers} icon="👥" color="blue" />
                  <StatCard title="Resume Forwarded" value={docladderStats.totalForwarded} icon="📨" color="purple" />
                  <StatCard title="Selected" value={docladderStats.totalSelected} icon="✅" color="green" />
                  <StatCard title="Joined" value={docladderStats.totalJoined} icon="🤝" color="green" />
                  <StatCard title="Revenue" value={`${docladderStats.totalRevenue} lac`} icon="💰" color="green" />
                  <StatCard title="Conversion Rate" value={`${docladderStats.avgConversionRate}%`} icon="📊" color="indigo" />
                </div>

                {/* Top Performers */}
                <div className="mt-6 bg-white rounded-lg shadow-md p-4">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">🏆 Top Performers</h3>
                  <TopPerformersTable data={docladderData} />
                </div>
              </>
            ) : (
              <div className="bg-white rounded-lg shadow-md p-6 text-center text-gray-500">
                You do not have access to Docladder analytics.
              </div>
            )
          )}

          {/* Team Leaders View */}
          {selectedView === "teamleader" && (
            canViewTeamLeader ? (
              <>
                <h2 className="text-2xl font-bold text-gray-700 mb-4">Team Leaders Analytics</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <StatCard title="Total Leaders" value={teamLeaderStats.totalUsers} icon="👥" color="blue" />
                  <StatCard title="Total Points Achieved" value={teamLeaderStats.totalPoints} icon="⭐" color="yellow" />
                  <StatCard title="Target Points" value={teamLeaderStats.totalTargetPoints} icon="🎯" color="purple" />
                  <StatCard title="Avg Performance" value={`${teamLeaderStats.avgPerformance}%`} icon="📊" color="indigo" />
                </div>

                {/* Top Performers */}
                <div className="mt-6 bg-white rounded-lg shadow-md p-4">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">🏆 Top Performing Leaders</h3>
                  <TeamLeaderPerformersTable data={teamLeaderData} />
                </div>
              </>
            ) : (
              <div className="bg-white rounded-lg shadow-md p-6 text-center text-gray-500">
                You do not have access to Team Leader analytics.
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

// Stat Card Component - Enhanced
const StatCard = ({ title, value, icon, color }) => {
  const colorClasses = {
    blue: "bg-gradient-to-br from-blue-50 to-blue-100 border-blue-300 text-blue-700 hover:shadow-lg",
    purple: "bg-gradient-to-br from-purple-50 to-purple-100 border-purple-300 text-purple-700 hover:shadow-lg",
    yellow: "bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-300 text-yellow-700 hover:shadow-lg",
    green: "bg-gradient-to-br from-green-50 to-green-100 border-green-300 text-green-700 hover:shadow-lg",
    red: "bg-gradient-to-br from-red-50 to-red-100 border-red-300 text-red-700 hover:shadow-lg",
    indigo: "bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-300 text-indigo-700 hover:shadow-lg",
    teal: "bg-gradient-to-br from-teal-50 to-teal-100 border-teal-300 text-teal-700 hover:shadow-lg",
  };

  return (
    <div className={`${colorClasses[color]} border-2 rounded-xl p-5 shadow-md transition-all duration-300 hover:scale-105 cursor-pointer`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold opacity-60 uppercase tracking-wide">{title}</p>
          <p className="text-3xl font-bold mt-2">{value}</p>
        </div>
        <div className="text-5xl opacity-40 transform hover:scale-110 transition-transform">{icon}</div>
      </div>
    </div>
  );
};

// Department Card Component - Enhanced
const DepartmentCard = ({ title, stats, color }) => {
  return (
    <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-lg p-6 border-t-4 border-blue-500 hover:shadow-2xl transition-all duration-300">
      <h3 className="text-2xl font-bold text-gray-800 mb-5 flex items-center gap-2">
        {title === "HR Associates" ? "👔" : "🪜"} {title}
      </h3>
      <div className="space-y-3">
        <div className="flex justify-between items-center bg-white rounded-lg p-2 shadow-sm">
          <span className="text-gray-600 font-medium">Total Users:</span>
          <span className="font-bold text-lg text-blue-600">{stats.totalUsers}</span>
        </div>
        <div className="flex justify-between items-center bg-white rounded-lg p-2 shadow-sm">
          <span className="text-gray-600 font-medium">Resume Forwarded:</span>
          <span className="font-bold text-lg text-purple-600">{stats.totalForwarded}</span>
        </div>
        <div className="flex justify-between items-center bg-white rounded-lg p-2 shadow-sm">
          <span className="text-gray-600 font-medium">Pipeline:</span>
          <span className="font-bold text-lg text-yellow-600">{stats.totalPipeline}</span>
        </div>
        <div className="flex justify-between items-center bg-white rounded-lg p-2 shadow-sm">
          <span className="text-gray-600 font-medium">Selected:</span>
          <span className="font-bold text-lg text-green-600">✅ {stats.totalSelected}</span>
        </div>
        <div className="flex justify-between items-center bg-white rounded-lg p-2 shadow-sm">
          <span className="text-gray-600 font-medium">Joined:</span>
          <span className="font-bold text-lg text-emerald-600">🤝 {stats.totalJoined}</span>
        </div>
        <div className="flex justify-between items-center bg-white rounded-lg p-2 shadow-sm">
          <span className="text-gray-600 font-medium">Backout:</span>
          <span className="font-bold text-lg text-red-600">❌ {stats.totalBackout}</span>
        </div>
        <div className="flex justify-between items-center bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-3 shadow-md border-2 border-green-300">
          <span className="text-gray-700 font-semibold">Revenue:</span>
          <span className="font-bold text-xl text-green-700">💰 {stats.totalRevenue} lac</span>
        </div>
        <div className="flex justify-between items-center bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-3 shadow-md border-2 border-blue-300 mt-4">
          <span className="text-gray-700 font-semibold">Conversion Rate:</span>
          <span className="font-bold text-xl text-blue-700">📊 {stats.avgConversionRate}%</span>
        </div>
      </div>
    </div>
  );
};

// Top Performers Table - Enhanced
const TopPerformersTable = ({ data }) => {
  const sortedData = [...data]
    .sort((a, b) => (parseFloat(b.expected_revenue || b.revenue || 0) - parseFloat(a.expected_revenue || a.revenue || 0)))
    .slice(0, 10);

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full bg-white rounded-lg overflow-hidden">
        <thead className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
          <tr>
            <th className="py-3 px-4 text-left font-semibold">Rank</th>
            <th className="py-3 px-4 text-left font-semibold">Name</th>
            <th className="py-3 px-4 text-center font-semibold">Forwarded</th>
            <th className="py-3 px-4 text-center font-semibold">Selected</th>
            <th className="py-3 px-4 text-center font-semibold">Joined</th>
            <th className="py-3 px-4 text-center font-semibold">Revenue</th>
            <th className="py-3 px-4 text-center font-semibold">Conversion %</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {sortedData.map((record, index) => {
            const totalRecordCandidates = (record.resume_forwarded || 0) + (record.pipeline || 0) + (record.turned_up || 0) + (record.selected || 0);
            const conversionRate =
              totalRecordCandidates > 0
                ? ((record.selected / totalRecordCandidates) * 100).toFixed(2)
                : 0;

            const rowBg = index === 0 ? "bg-yellow-50" : index === 1 ? "bg-gray-50" : index === 2 ? "bg-orange-50" : "";

            return (
              <tr key={record.user || index} className={`hover:bg-blue-50 transition-colors ${rowBg}`}>
                <td className="py-3 px-4">
                  <span className="text-2xl">
                    {index === 0 && "🥇"}
                    {index === 1 && "🥈"}
                    {index === 2 && "🥉"}
                    {index > 2 && (
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gray-200 text-gray-700 font-semibold">
                        {index + 1}
                      </span>
                    )}
                  </span>
                </td>
                <td className="py-3 px-4 font-semibold text-gray-800">{record.user_name || "N/A"}</td>
                <td className="py-3 px-4 text-center font-medium text-gray-700">{record.resume_forwarded || 0}</td>
                <td className="py-3 px-4 text-center font-medium text-gray-700">{record.selected || 0}</td>
                <td className="py-3 px-4 text-center font-medium text-emerald-700">{record.joined || 0}</td>
                <td className="py-3 px-4 text-center text-green-700 font-bold text-lg">
                  💰 {(record.revenue || 0).toFixed(2)} lac
                </td>
                <td className="py-3 px-4 text-center">
                  <span
                    className={`px-3 py-1 rounded-full font-semibold text-sm ${conversionRate >= 20
                      ? "bg-gradient-to-r from-green-100 to-green-200 text-green-800 border border-green-300"
                      : conversionRate >= 10
                        ? "bg-gradient-to-r from-yellow-100 to-yellow-200 text-yellow-800 border border-yellow-300"
                        : "bg-gradient-to-r from-red-100 to-red-200 text-red-800 border border-red-300"
                      }`}
                  >
                    {conversionRate}%
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {sortedData.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          <div className="text-6xl mb-4">📊</div>
          <div className="text-lg">No data available for this period</div>
        </div>
      )}
    </div>
  );
};

// Team Leader Card Component
const TeamLeaderCard = ({ title, stats, color }) => {
  return (
    <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-lg p-6 border-t-4 border-indigo-500 hover:shadow-2xl transition-all duration-300">
      <h3 className="text-2xl font-bold text-gray-800 mb-5 flex items-center gap-2">
        👔 {title}
      </h3>
      <div className="space-y-3">
        <div className="flex justify-between items-center bg-white rounded-lg p-2 shadow-sm">
          <span className="text-gray-600 font-medium">Total Leaders:</span>
          <span className="font-bold text-lg text-blue-600">{stats.totalUsers}</span>
        </div>
        <div className="flex justify-between items-center bg-white rounded-lg p-2 shadow-sm">
          <span className="text-gray-600 font-medium">Points Achieved:</span>
          <span className="font-bold text-lg text-yellow-600">⭐ {stats.totalPoints}</span>
        </div>
        <div className="flex justify-between items-center bg-white rounded-lg p-2 shadow-sm">
          <span className="text-gray-600 font-medium">Target Points:</span>
          <span className="font-bold text-lg text-purple-600">🎯 {stats.totalTargetPoints}</span>
        </div>
        <div className="flex justify-between items-center bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-3 shadow-md border-2 border-blue-300 mt-4">
          <span className="text-gray-700 font-semibold">Avg Performance:</span>
          <span className="font-bold text-xl text-blue-700">📊 {stats.avgPerformance}%</span>
        </div>
      </div>
    </div>
  );
};

// Team Leader Performers Table
const TeamLeaderPerformersTable = ({ data }) => {
  // Sort by calculatePercentage (same as showTeamLeader page) in descending order
  const sortedData = [...(data || [])]
    .sort((a, b) => (parseFloat(b.calculatePercentage) || 0) - (parseFloat(a.calculatePercentage) || 0))
    .slice(0, 10);

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full bg-white rounded-lg overflow-hidden">
        <thead className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
          <tr>
            <th className="py-3 px-4 text-left font-semibold">Rank</th>
            <th className="py-3 px-4 text-left font-semibold">Name</th>
            <th className="py-3 px-4 text-center font-semibold">Department</th>
            <th className="py-3 px-4 text-center font-semibold">Points Achieved</th>
            <th className="py-3 px-4 text-center font-semibold">Target Points</th>
            <th className="py-3 px-4 text-center font-semibold">Performance %</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {sortedData.map((leader, index) => {
            // Use calculatePercentage if available, otherwise calculate it
            const performance = leader.calculatePercentage !== undefined
              ? parseFloat(leader.calculatePercentage).toFixed(2)
              : parseFloat(leader.targets_points_sum) > 0
                ? ((parseFloat(leader.value_points) / parseFloat(leader.targets_points_sum)) * 100).toFixed(2)
                : 0;

            const rowBg = index === 0 ? "bg-yellow-50" : index === 1 ? "bg-gray-50" : index === 2 ? "bg-orange-50" : "";

            return (
              <tr key={leader.id || index} className={`hover:bg-blue-50 transition-colors ${rowBg}`}>
                <td className="py-3 px-4">
                  <span className="text-2xl">
                    {index === 0 && "🥇"}
                    {index === 1 && "🥈"}
                    {index === 2 && "🥉"}
                    {index > 2 && (
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gray-200 text-gray-700 font-semibold">
                        {index + 1}
                      </span>
                    )}
                  </span>
                </td>
                <td className="py-3 px-4 font-semibold text-gray-800">{leader.name || "N/A"}</td>
                <td className="py-3 px-4 text-center font-medium text-gray-700">{leader.dept || "N/A"}</td>
                <td className="py-3 px-4 text-center text-yellow-700 font-bold text-lg">
                  ⭐ {parseFloat(leader.value_points || 0).toFixed(2)}
                </td>
                <td className="py-3 px-4 text-center text-purple-700 font-bold text-lg">
                  🎯 {parseFloat(leader.targets_points_sum || 0).toFixed(2)}
                </td>
                <td className="py-3 px-4 text-center">
                  <span
                    className={`px-3 py-1 rounded-full font-semibold text-sm ${performance >= 80
                      ? "bg-gradient-to-r from-green-100 to-green-200 text-green-800 border border-green-300"
                      : performance >= 60
                        ? "bg-gradient-to-r from-yellow-100 to-yellow-200 text-yellow-800 border border-yellow-300"
                        : "bg-gradient-to-r from-red-100 to-red-200 text-red-800 border border-red-300"
                      }`}
                  >
                    {performance}%
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {sortedData.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          <div className="text-6xl mb-4">📊</div>
          <div className="text-lg">No data available for this period</div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsDashboard;

